/**
 * Language for AI-generated content (social posts, blogs, courses, suggestions …).
 *
 * Default: the site language picked in the top-right language menu.
 * The user can override it in the same menu ("Generate content in").
 *
 * Every request to the Ocesha API carries   X-Content-Language: <code>
 * (axios + fetch, so RTK Query too). The backend adds a "write in <language>"
 * instruction to every LLM call of that request, including its Celery tasks.
 */
import axios from "axios";
import { LANGUAGES, getCurrentLang } from "./googleTranslate";

export const CONTENT_LANGUAGE_AUTO = "auto";
const STORAGE_KEY = "content_language";
const HEADER = "X-Content-Language";
const API_BASE = (process.env.REACT_APP_SERVER_ENDPOINT || "").replace(/\/+$/, "");

const isSupported = (code: string) => LANGUAGES.some((l) => l.code === code);

/** "auto" (follow the site language) or a language code */
export const getContentLanguageSetting = (): string => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && (v === CONTENT_LANGUAGE_AUTO || isSupported(v)) ? v : CONTENT_LANGUAGE_AUTO;
  } catch {
    return CONTENT_LANGUAGE_AUTO;
  }
};

/** The language content is generated in right now */
export const getContentLanguage = (): string => {
  const setting = getContentLanguageSetting();
  const code = setting === CONTENT_LANGUAGE_AUTO ? getCurrentLang() : setting;
  return isSupported(code) ? code : "en";
};

/**
 * Language changed: the dashboard suggestions cached in the old language are kept aside
 * per language ("authority_engine_cache__<lang>") instead of being thrown away, so the
 * dashboard can translate them (stored translations, no regeneration) or reuse the set
 * it already has for the new language.
 */
export const clearLanguageDependentCaches = () => {
  try {
    const raw = localStorage.getItem("authority_engine_cache");
    if (raw) {
      const lang = (JSON.parse(raw) || {}).__lang || "en";
      localStorage.setItem(`authority_engine_cache__${lang}`, raw);
    }
    localStorage.removeItem("authority_engine_cache");
  } catch { /* ignore */ }
};

export const setContentLanguageSetting = (value: string) => {
  const before = getContentLanguage();
  try {
    localStorage.setItem(STORAGE_KEY, value === CONTENT_LANGUAGE_AUTO || isSupported(value) ? value : CONTENT_LANGUAGE_AUTO);
  } catch { /* ignore */ }
  if (getContentLanguage() !== before) clearLanguageDependentCaches();
  window.dispatchEvent(new CustomEvent("content-language-changed", { detail: getContentLanguage() }));
};

// ------------------------------------------------------------------ header on API calls
const isApiUrl = (url: string) => {
  if (!API_BASE) return false;
  try { return new URL(url, window.location.href).href.startsWith(API_BASE); } catch { return false; }
};

let installed = false;
export const installContentLanguageHeader = () => {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use((config) => {
    const full = `${config.baseURL || ""}${config.url || ""}`;
    if (isApiUrl(full)) {
      config.headers = config.headers || {};
      if (!(config.headers as any)[HEADER]) (config.headers as any)[HEADER] = getContentLanguage();
    }
    return config;
  });

  const origFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    try {
      return withLanguageHeader(input, init);
    } catch (e) {
      // never let the language header break a request (sign-in, payments …)
      console.warn("content-language header skipped", e);
      return origFetch(input as any, init);
    }
  };

  const withLanguageHeader = (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    if (!isApiUrl(url)) return origFetch(input as any, init);
    if (input instanceof Request) {
      // RTK Query (sign-in, sign-up …) passes a Request object with a body. Don't rebuild
      // the Request (re-wrapping its body can fail in some browsers); pass the merged
      // headers through init instead — the original Request, body and credentials stay as they are.
      if (input.headers.has(HEADER)) return origFetch(input, init);
      const headers = new Headers(init?.headers || input.headers);
      headers.set(HEADER, getContentLanguage());
      return origFetch(input, { ...(init || {}), headers });
    }
    const headers = new Headers(init?.headers || {});
    if (!headers.has(HEADER)) headers.set(HEADER, getContentLanguage());
    return origFetch(input as any, { ...(init || {}), headers });
  };
};
